import { useMemo, useRef, useState } from 'react';
import { Flag, Paperclip, Send, Loader2, X, Target, CalendarCheck, CalendarDays, CheckCircle2 } from 'lucide-react';
import { format, parseISO, addMonths } from 'date-fns';
import { Entry, ENTRY_TYPES } from './Entry';
import { AuthedImg } from './AuthedImg';
import { MentionInput, mentionIds } from './Mention';
import { useBiz } from '../BizContext';
import { isOpenGoal } from '../lib';

const FILTERS = [
  { key: 'all', label: 'All' }, { key: 'pinned', label: 'Pinned' }, { key: 'urgent', label: 'Needs action' }, { key: 'note', label: 'Notes' }, { key: 'idea', label: 'Ideas' },
  { key: 'decision', label: 'Decisions' }, { key: 'link', label: 'Links' }, { key: 'photo', label: 'Photos' }, { key: 'file', label: 'Files' }, { key: 'review', label: 'Reviews' }, { key: 'timeline', label: 'Timeline' },
];
const SORTS = [{ key: 'newest', label: 'Newest first' }, { key: 'updated', label: 'Recently updated' }, { key: 'urgent', label: 'Most urgent' }, { key: 'oldest', label: 'Oldest first' }];
const COMPOSER_TYPES = ['note', 'idea', 'decision', 'link', 'photo', 'file'];
const ACCEPT = { photo: 'image/*,.heic,.heif', file: '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,application/pdf' };
const PLACEHOLDER = { note: 'What happened, what was said, what you’re thinking…', idea: 'A thought worth keeping — turn it into an initiative later', decision: 'What was decided, and why', link: 'What is this link and why does it matter?', photo: 'Caption for the photo(s) — optional', file: 'What’s in the document? — optional' };
const lastActivity = (e) => [e.updated_at, ...(e.comments || []).map((c) => c.at)].sort().pop();

function Composer({ onPost, onUpload, progress }) {
  const { owners } = useBiz();
  const [type, setType] = useState('note');
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [needs, setNeeds] = useState(false);
  const [needsText, setNeedsText] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const t = ENTRY_TYPES[type];
  const isFile = type === 'photo' || type === 'file';
  const reset = () => { setText(''); setUrl(''); setNeeds(false); setNeedsText(''); setFiles([]); };
  const post = async () => {
    if (isFile && !files.length) { fileRef.current?.click(); return; }
    if (!isFile && !text.trim() && !url.trim()) return;
    setBusy(true);
    const ok = isFile
      ? await onUpload(files, { text: text.trim(), urgent: needs, needs: needsText.trim() })
      : await onPost({ type, text: text.trim(), links: url.trim() ? [{ url: url.trim() }] : [], urgent: needs, needs: needsText.trim(), mentions: mentionIds(text, owners) });
    setBusy(false);
    if (ok) reset();
  };
  const addFiles = (list) => { const arr = Array.from(list || []); if (!arr.length) return; setFiles((f) => [...f, ...arr].slice(0, 12)); };
  return (
    <div className="biz-card p-4" style={{ borderColor: type === 'note' ? 'var(--line)' : t.bg }} data-testid="ws-composer"
      onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); } }} onDrop={(e) => { if (e.dataTransfer.files?.length) { e.preventDefault(); if (!isFile) setType(Array.from(e.dataTransfer.files).every((f) => f.type.startsWith('image/')) ? 'photo' : 'file'); addFiles(e.dataTransfer.files); } }}>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {COMPOSER_TYPES.map((k) => { const c = ENTRY_TYPES[k]; const on = k === type; return (
          <button key={k} type="button" onClick={() => setType(k)} className="biz-chip transition-colors" style={{ background: on ? c.bg : 'transparent', color: on ? c.color : 'var(--ink-3)', border: `1px solid ${on ? 'transparent' : 'var(--line)'}` }} data-testid={`ws-compose-${k}`}><c.Icon className="w-3 h-3" /> {c.label}</button>
        ); })}
      </div>
      <div className="flex gap-3">
        <span className="w-9 h-9 rounded-full hidden sm:flex items-center justify-center shrink-0 mt-0.5" style={{ background: t.bg, color: t.color }}><t.Icon className="w-4 h-4" /></span>
        <div className="flex-1 flex flex-col gap-2 min-w-0">
          <MentionInput multiline value={text} onChange={setText} rows={2} placeholder={`${PLACEHOLDER[type]} Type @ to mention a colleague.`} className="biz-input resize-none w-full" testId="ws-compose-text" />
          {type === 'link' && <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="biz-input" data-testid="ws-compose-url" />}
          {isFile && (
            <>
              <input ref={fileRef} type="file" multiple accept={ACCEPT[type]} className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="ws-compose-file-input" />
              <button type="button" onClick={() => fileRef.current?.click()} className="rounded-xl py-4 text-center text-xs w-full" style={{ border: '1px dashed var(--line-2)', color: 'var(--ink-3)' }} data-testid="ws-compose-drop">
                <Paperclip className="w-4 h-4 inline mr-1" /> {type === 'photo' ? 'Choose photos or screenshots' : 'Choose PDFs, Word or Excel files'} — or drop them here
              </button>
              {files.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="ws-compose-files">
                  {files.map((f, i) => <span key={i} className="biz-chip" style={{ background: 'var(--paper-3)', color: 'var(--ink-2)' }}>{f.name.length > 28 ? f.name.slice(0, 26) + '…' : f.name}<button type="button" onClick={() => setFiles((l) => l.filter((_, j) => j !== i))} className="ml-1"><X className="w-3 h-3" /></button></span>)}
                </div>
              )}
              {progress != null && <div className="biz-bar"><span style={{ width: `${progress}%`, background: 'var(--apricot)' }} /></div>}
            </>
          )}
          {needs && <input value={needsText} onChange={(e) => setNeedsText(e.target.value)} placeholder="What’s needed, and by when?" className="biz-input" data-testid="ws-compose-needs-text" />}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button type="button" onClick={() => setNeeds((v) => !v)} className="biz-chip" style={{ background: needs ? 'var(--apricot-soft)' : 'transparent', color: needs ? 'var(--apricot-deep)' : 'var(--ink-3)', border: `1px solid ${needs ? 'transparent' : 'var(--line)'}` }} data-testid="ws-compose-needs"><Flag className="w-3 h-3" /> Needs action</button>
            <button type="button" onClick={post} disabled={busy} className="biz-btn biz-btn-primary text-xs py-2" data-testid="ws-compose-post">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} {isFile && !files.length ? 'Choose files' : 'Post'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Timeline({ entries, milestones }) {
  const rows = [
    ...milestones.map((m) => ({ ...m, key: m.text, future: true })),
    ...entries.map((e) => ({ at: e.at, text: e.type === 'system' ? e.text : `${ENTRY_TYPES[e.type]?.label || 'Note'}: ${e.text || (e.files?.length ? `${e.files.length} file${e.files.length === 1 ? '' : 's'}` : '')}`, Icon: (ENTRY_TYPES[e.type] || ENTRY_TYPES.note).Icon, tone: (ENTRY_TYPES[e.type] || ENTRY_TYPES.note).color, key: e.id })),
  ].filter((r) => r.at).sort((a, b) => (a.at < b.at ? 1 : -1));
  let month = null;
  return (
    <ol className="relative pl-6 flex flex-col gap-3" style={{ borderLeft: '2px solid var(--paper-3)' }} data-testid="ws-timeline">
      {rows.length === 0 && <li className="text-xs" style={{ color: 'var(--ink-3)' }}>Nothing on the timeline yet.</li>}
      {rows.map((r) => {
        const m = format(parseISO(r.at), 'MMMM yyyy'); const head = m !== month; month = m;
        return (
          <li key={r.key} className="relative">
            {head && <p className="text-xs uppercase tracking-wider font-semibold mb-2 -ml-1" style={{ color: 'var(--ink-3)' }}>{m}</p>}
            <span className="absolute -left-[31px] top-0.5 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: 'var(--paper-2)', border: `2px ${r.future ? 'dashed' : 'solid'} ${r.tone}` }}><r.Icon className="w-2.5 h-2.5" style={{ color: r.tone }} /></span>
            <p className="text-xs" style={{ color: r.future ? 'var(--ink-3)' : 'var(--ink)' }}><span className="font-semibold tabular-nums mr-2" style={{ color: 'var(--ink-3)' }}>{format(parseISO(r.at), 'dd/MM/yyyy')}</span>{r.text.length > 120 ? r.text.slice(0, 119) + '…' : r.text}</p>
          </li>
        );
      })}
    </ol>
  );
}

export function milestonesFor(kind, entity) {
  const today = new Date();
  if (kind === 'goal') {
    const ms = [];
    if (entity.review_date && isOpenGoal(entity)) {
      const due = parseISO(entity.review_date);
      ms.push({ at: entity.review_date, text: `Review sit-down${due < today ? ' · overdue' : ''}`, Icon: CalendarCheck, tone: due < today ? 'var(--rose-deep)' : 'var(--sage-deep)' });
      ms.push({ at: format(addMonths(due, entity.review_every_months || 3), 'yyyy-MM-dd'), text: 'Following review', Icon: CalendarCheck, tone: 'var(--ink-3)' });
    }
    if (entity.target_date) ms.push({ at: entity.target_date, text: 'Target date', Icon: Target, tone: 'var(--apricot-deep)' });
    return ms;
  }
  const ms = [];
  if (entity.due && entity.status !== 'done') ms.push({ at: entity.due, text: `Due${parseISO(entity.due) < today ? ' · overdue' : ''}`, Icon: CalendarDays, tone: parseISO(entity.due) < today ? 'var(--rose-deep)' : 'var(--apricot-deep)' });
  if (entity.completed_at) ms.push({ at: entity.completed_at.slice(0, 10), text: 'Completed', Icon: CheckCircle2, tone: 'var(--sage-deep)' });
  return ms;
}

export function Journal({ kind, entity, journal, canPromote, onOpenItem }) {
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('newest');
  const [lightbox, setLightbox] = useState(null);
  const entries = journal.entries;
  const visible = useMemo(() => {
    if (!entries) return [];
    let list = entries.filter((e) => e.type !== 'system' || sort === 'oldest');
    if (filter === 'pinned') list = list.filter((e) => e.pinned);
    else if (filter === 'urgent') list = list.filter((e) => e.urgent);
    else if (filter !== 'all' && filter !== 'timeline') list = list.filter((e) => e.type === filter);
    const score = (e) => (e.urgent ? 4 : 0) + (e.pinned ? 2 : 0) + (e.type === 'decision' ? 1 : 0);
    list = [...list].sort((a, b) => {
      if (sort === 'urgent') return score(b) - score(a) || (a.at < b.at ? 1 : -1);
      if (sort === 'updated') return lastActivity(a) < lastActivity(b) ? 1 : -1;
      if (sort === 'oldest') return a.at > b.at ? 1 : -1;
      return a.at < b.at ? 1 : -1;
    });
    return sort === 'oldest' || sort === 'urgent' ? list : [...list.filter((e) => e.pinned), ...list.filter((e) => !e.pinned)];
  }, [entries, filter, sort]);
  const counts = useMemo(() => ({ pinned: entries?.filter((e) => e.pinned).length || 0, urgent: entries?.filter((e) => e.urgent).length || 0 }), [entries]);

  return (
    <div className="flex flex-col gap-4" data-testid="ws-journal">
      <Composer onPost={journal.post} onUpload={journal.upload} progress={journal.progress} />
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex flex-wrap gap-1" data-testid="ws-filters">
          {FILTERS.map((f) => { const n = counts[f.key]; return (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)} className="text-xs px-2.5 py-1 rounded-full font-medium transition-colors" style={{ background: filter === f.key ? 'var(--ink)' : 'transparent', color: filter === f.key ? 'var(--paper)' : 'var(--ink-2)' }} data-testid={`ws-filter-${f.key}`}>{f.label}{n ? <span className="ml-1 opacity-60">{n}</span> : null}</button>
          ); })}
        </div>
        {filter !== 'timeline' && <select value={sort} onChange={(e) => setSort(e.target.value)} className="biz-input ml-auto text-xs py-1.5" style={{ width: 'auto' }} data-testid="ws-sort">{SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</select>}
      </div>
      {!entries ? (
        <p className="text-sm text-center py-8" style={{ color: 'var(--ink-3)' }}><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Loading the journal…</p>
      ) : filter === 'timeline' ? (
        <div className="biz-card p-5"><Timeline entries={entries} milestones={milestonesFor(kind, entity)} /></div>
      ) : (
        <div className="flex flex-col gap-3" data-testid="ws-entries">
          {visible.length === 0 && <p className="text-sm text-center py-8" style={{ color: 'var(--ink-3)' }}>{entries.length <= 1 ? 'The journal is empty — the first note usually explains why this is on the list.' : 'Nothing matches that filter.'}</p>}
          {visible.map((e) => (
            <Entry key={e.id} e={e} canPromote={canPromote} onPin={(id, v) => journal.patch(id, { pinned: v })} onFlag={(id, v) => journal.patch(id, { urgent: v })} onEdit={journal.patch}
              onPromote={journal.promote} onDelete={journal.remove} onComment={journal.comment} onUncomment={journal.uncomment} onOpenImage={setLightbox} onOpenItem={onOpenItem} />
          ))}
        </div>
      )}
      {lightbox && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-6" style={{ background: 'rgba(42,39,34,0.88)' }} onClick={() => setLightbox(null)} data-testid="ws-lightbox">
          <AuthedImg src={lightbox.src} alt={lightbox.name} className="max-w-full max-h-full rounded-xl shadow-2xl object-contain" />
          <button type="button" onClick={() => setLightbox(null)} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20" data-testid="ws-lightbox-close"><X className="w-5 h-5" /></button>
        </div>
      )}
    </div>
  );
}
